import React, { useState, useEffect } from 'react';
import { useApp, apiFetch } from '../../context/AppContext';
import { Card, Button, Input } from '../../components/common/UI';
import { Mail, Building2, Save, RotateCcw, Send, Layout, Info, ArrowLeft, CheckCircle } from 'lucide-react';


const MOCK_BODY = `<h3 style="color: #1e293b; font-size: 18px; margin-top: 0; margin-bottom: 12px; font-weight: 600;">Action Required: Gap Assessment Review</h3>
<p style="color: #475569; font-size: 14px; margin: 0 0 16px 0;">Hello team member,</p>
<p style="color: #475569; font-size: 14px; margin: 0 0 16px 0;">Your organization administrator has requested a review of your department's compliance assessments under the Digital Personal Data Protection (DPDP) Act.</p>

<div style="margin: 24px 0; padding: 16px; background-color: #eff6ff; border-radius: 8px; border-left: 4px solid #2563eb;">
  <p style="margin: 0 0 4px 0; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; color: #2563eb; font-weight: 700;">Task Assignment Details</p>
  <p style="margin: 0; font-size: 13px; color: #1e3a8a; font-weight: 500;">
    Module: ROPA &amp; Consent Audit<br/>
    Assigned Department: Human Resources
  </p>
</div>

<p style="color: #475569; font-size: 14px; margin: 0;">Please follow your company's internal portal URL to complete this validation.</p>`;

export default function TenantEmailTemplatesPage() {
  const { showNotification } = useApp();
  const currentYear = new Date().getFullYear();
  const defaultCopyright = `© ${currentYear} OneDPDP Technologies Inc. All rights reserved.`;

  const [primaryColor, setPrimaryColor] = useState('#2563eb');
  const [secondaryColor, setSecondaryColor] = useState('#1d4ed8');
  const [companyName, setCompanyName] = useState('Your Portal');
  const [copyrightText, setCopyrightText] = useState('All data is securely handled in compliance with DPDP regulations.');
  const [disclaimerText, setDisclaimerText] = useState('This email was sent by your organization via OneDPDP.');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  // Fetch email templates
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/tenant/settings/email-template');
        if (res.ok) {
          const data = await res.json();
          if (data.email_header) {
            try {
              const hCfg = JSON.parse(data.email_header);
              setPrimaryColor(hCfg.primary_color || '#2563eb');
              setSecondaryColor(hCfg.secondary_color || '#1d4ed8');
              setCompanyName(hCfg.company_name || 'Your Portal');
            } catch {
              if (data.email_header.startsWith('#')) {
                setPrimaryColor(data.email_header);
                setSecondaryColor(data.email_header);
              } else if (data.email_header) {
                setCompanyName(data.email_header);
              }
            }
          }
          if (data.email_footer) {
            try {
              const fCfg = JSON.parse(data.email_footer);
              setCopyrightText(fCfg.copyright_text || 'All data is securely handled in compliance with DPDP regulations.');
              setDisclaimerText(fCfg.disclaimer_text || 'This email was sent by your organization via OneDPDP.');
            } catch {
              setCopyrightText(data.email_footer);
            }
          }
        } else {
          showNotification('Failed to load email template', 'error');
        }
      } catch (err) {
        showNotification('Network error loading templates', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [showNotification]);

  // Save changes
  const handleSave = async (silent = false) => {
    if (!silent) setSaving(true);
    try {
      const res = await apiFetch('/tenant/settings/email-template', {
        method: 'PATCH',
        body: JSON.stringify({
          email_header: JSON.stringify({
            primary_color: primaryColor,
            secondary_color: secondaryColor,
            company_name: companyName,
          }),
          email_footer: JSON.stringify({
            copyright_text: copyrightText,
            disclaimer_text: disclaimerText,
          }),
        }),
      });
      if (res.ok) {
        if (!silent) showNotification('Organization email templates saved successfully');
        return true;
      } else {
        const err = await res.json();
        if (!silent) showNotification(err.detail || 'Failed to update templates', 'error');
        return false;
      }
    } catch {
      if (!silent) showNotification('Network error saving templates', 'error');
      return false;
    } finally {
      if (!silent) setSaving(false);
    }
  };

  // Reset to default presets
  const handleResetToDefault = () => {
    setPrimaryColor('#2563eb');
    setSecondaryColor('#1d4ed8');
    setCompanyName('Your Portal');
    setCopyrightText('All data is securely handled in compliance with DPDP regulations.');
    setDisclaimerText('This email was sent by your organization via OneDPDP.');
    showNotification('Loaded preset organization templates. Save to apply.', 'info');
  };

  // Clear templates (fall back to platform templates)
  const handleClear = () => {
    // When clearing/resetting we can set them to the default platform values or empty
    // But setting them to a clean clear status means we save empty string configurations in the DB.
    // That way, it falls back to platform settings!
    setPrimaryColor('');
    setSecondaryColor('');
    setCompanyName('');
    setCopyrightText('');
    setDisclaimerText('');
    showNotification('Templates cleared. Save to fall back on Platform Default templates.', 'info');
  };

  // Apply preset color combinations
  const applyPreset = (primary, secondary) => {
    setPrimaryColor(primary);
    setSecondaryColor(secondary);
    showNotification('Applied color preset', 'info');
  };

  // Send test email
  const handleSendTest = async (e) => {
    e.preventDefault();
    if (!testEmail || !/\S+@\S+\.\S+/.test(testEmail)) {
      showNotification('Please enter a valid email address to test', 'error');
      return;
    }
    setTesting(true);
    try {
      // Save templates first
      const saved = await handleSave(true);
      if (!saved) {
        showNotification('Failed to save templates before testing', 'error');
        setTesting(false);
        return;
      }

      // Trigger standard SMTP test
      const res = await apiFetch('/tenant/settings/smtp/test', {
        method: 'POST',
        body: JSON.stringify({ to_email: testEmail }),
      });

      if (res.ok) {
        showNotification(`Test email successfully sent to ${testEmail}`);
      } else {
        const err = await res.json();
        showNotification(err.detail || 'Failed to send test email', 'error');
      }
    } catch {
      showNotification('Network error executing SMTP test', 'error');
    } finally {
      setTesting(false);
    }
  };

  // Compile full HTML body for local preview
  const getPreviewHtml = () => {
    // If tenant template properties are empty, they fall back to platform default templates
    // For the preview, if they are empty, we display the platform default template
    const pColor = primaryColor || '#4f46e5';
    const sColor = secondaryColor || '#7c3aed';
    const cName = companyName || 'OneDPDP Privacy Hub';
    const copText = copyrightText || defaultCopyright;
    const discText = disclaimerText || 'This email was sent by OneDPDP on behalf of your organization.';

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
</head>
<body style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #1e293b; line-height: 1.6; margin: 0; padding: 0; background-color: #f8fafc;">
  <div style="max-width: 600px; margin: 20px auto; background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);">
    <div style="background: linear-gradient(135deg, ${pColor} 0%, ${sColor} 100%); padding: 32px 24px; text-align: center;">
      <h1 style="color: #ffffff; margin: 0; font-size: 24px; font-weight: 700; letter-spacing: -0.5px;">${cName}</h1>
      <p style="color: rgba(255, 255, 255, 0.8); margin: 4px 0 0 0; font-size: 14px;">Unified DPDP Act Compliance Platform</p>
    </div>
    <div style="padding: 32px 24px;">
      ${MOCK_BODY}
    </div>
    <div style="background-color: #f8fafc; padding: 24px; text-align: center; border-top: 1px solid #f1f5f9;">
      <p style="margin: 0; font-size: 12px; color: #64748b;">
        ${discText}
      </p>
      <p style="margin: 6px 0 0 0; font-size: 12px; color: #94a3b8;">
        ${copText}
      </p>
    </div>
  </div>
</body>
</html>`;
  };

  const isCustomActive = primaryColor || secondaryColor || companyName;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400 text-sm">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-2" />
        Loading email templates…
      </div>
    );
  }

  return (
    <div className="p-1 space-y-6 min-h-screen">
      {/* HEADER */}
      <div data-tour-id="tour-email-tpl-header" className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-700 transition"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
            <Mail size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Email Templates</h1>
            <p className="text-sm text-slate-400 flex items-center gap-1 mt-0.5">
              <Building2 size={11} /> Customize Email Headers and Footers for your Organization
            </p>
          </div>
        </div>
        <div data-tour-id="tour-email-tpl-actions" className="flex items-center gap-2">
          <Button
            variant="secondary"
            leftIcon={<RotateCcw size={14} />}
            onClick={handleResetToDefault}
          >
            Load Preset
          </Button>
          <Button
            variant="secondary"
            onClick={handleClear}
            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
          >
            Reset (Fallback to Platform)
          </Button>
          <Button
            variant="primary"
            leftIcon={<Save size={14} />}
            onClick={() => handleSave()}
            loading={saving}
          >
            Save Template
          </Button>
        </div>
      </div>

      {/* OVERRIDE STATUS BANNER */}
      <div data-tour-id="tour-email-tpl-status" className={`p-4 rounded-xl border flex items-center gap-3 text-sm transition-all ${
        isCustomActive 
          ? 'bg-blue-50 border-blue-200 text-blue-800' 
          : 'bg-slate-50 border-slate-200 text-slate-700'
      }`}>
        <div className={`p-1.5 rounded-lg ${isCustomActive ? 'bg-blue-100' : 'bg-slate-200'}`}>
          {isCustomActive ? <CheckCircle size={14} className="text-blue-600" /> : <Info size={14} className="text-slate-600" />}
        </div>
        <div>
          <span className="font-semibold">Template Status: </span>
          {isCustomActive ? (
            <span><strong>Custom Overrides Active.</strong> Your users will receive emails styled with the header and footer defined below.</span>
          ) : (
            <span><strong>Fallback to Platform default.</strong> No custom headers/footers configured; emails will fall back to platform default branding.</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* EDITORS - LEFT PANEL */}
        <div className="xl:col-span-6 space-y-6">
          <div data-tour-id="tour-email-tpl-branding">
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <Layout size={15} className="text-blue-500" />
              <h2 className="text-sm font-semibold text-slate-800">Branding & Color Settings</h2>
            </div>
            
            <div className="space-y-5">
              {/* Presets */}
              <div className="space-y-2">
                <span className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Color Presets
                </span>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => applyPreset('#2563eb', '#1d4ed8')}
                    className="px-2.5 py-1 text-xs font-medium rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 border flex items-center gap-1.5"
                  >
                    <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-[#2563eb] to-[#1d4ed8]" />
                    Classic Blue (Default)
                  </button>
                  <button
                    onClick={() => applyPreset('#4f46e5', '#7c3aed')}
                    className="px-2.5 py-1 text-xs font-medium rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 border flex items-center gap-1.5"
                  >
                    <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-[#4f46e5] to-[#7c3aed]" />
                    Indigo/Violet
                  </button>
                  <button
                    onClick={() => applyPreset('#059669', '#0d9488')}
                    className="px-2.5 py-1 text-xs font-medium rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 border flex items-center gap-1.5"
                  >
                    <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-[#059669] to-[#0d9488]" />
                    Teal/Emerald
                  </button>
                  <button
                    onClick={() => applyPreset('#e11d48', '#be123c')}
                    className="px-2.5 py-1 text-xs font-medium rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 border flex items-center gap-1.5"
                  >
                    <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-[#e11d48] to-[#be123c]" />
                    Rose/Crimson
                  </button>
                  <button
                    onClick={() => applyPreset('#1e293b', '#0f172a')}
                    className="px-2.5 py-1 text-xs font-medium rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 border flex items-center gap-1.5"
                  >
                    <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-[#1e293b] to-[#0f172a]" />
                    Charcoal
                  </button>
                </div>
              </div>

              {/* Color Inputs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
                    Primary / Theme Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={primaryColor || '#2563eb'}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-slate-200 p-0"
                    />
                    <Input
                      type="text"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      placeholder="Use fallback"
                      className="flex-1 py-1 text-xs font-mono"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
                    Gradient End Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={secondaryColor || '#1d4ed8'}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border border-slate-200 p-0"
                    />
                    <Input
                      type="text"
                      value={secondaryColor}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                      placeholder="Use fallback"
                      className="flex-1 py-1 text-xs font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Branding Text */}
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Company / Organization Logo Text
                </label>
                <Input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Leave empty to use platform name"
                  className="w-full"
                />
              </div>

              {/* Footer text settings */}
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Footer Disclaimer
                </label>
                <textarea
                  rows={2}
                  value={disclaimerText}
                  onChange={(e) => setDisclaimerText(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white text-slate-800 p-3 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all leading-relaxed"
                  placeholder="Leave empty to use platform disclaimer"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Footer Copyright / Reg Info
                </label>
                <Input
                  type="text"
                  value={copyrightText}
                  onChange={(e) => setCopyrightText(e.target.value)}
                  placeholder="Leave empty to use platform copyright"
                  className="w-full"
                />
              </div>
            </div>
          </Card>
          </div>

          {/* SEND TEST EMAIL */}
          <div data-tour-id="tour-email-tpl-test">
          <Card>
            <div className="flex items-center gap-2 mb-2">
              <Send size={15} className="text-blue-500" />
              <h3 className="text-sm font-semibold text-slate-800">Send Test Email</h3>
            </div>
            <p className="text-xs text-slate-400 mb-4">
              Send a real test email to check your templates rendering. It will save the changes and trigger an SMTP check.
            </p>
            <form onSubmit={handleSendTest} className="flex gap-2">
              <div className="flex-1">
                <Input
                  type="email"
                  placeholder="test@example.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="py-2.5"
                  required
                />
              </div>
              <Button
                type="submit"
                variant="secondary"
                leftIcon={<Send size={13} />}
                loading={testing}
                className="px-5"
              >
                Send Test
              </Button>
            </form>
          </Card>
          </div>
        </div>

        {/* PREVIEW - RIGHT PANEL */}
        <div data-tour-id="tour-email-tpl-preview" className="xl:col-span-6 space-y-4">
          <div className="flex items-center gap-2 px-1 text-slate-700">
            <Info size={14} className="text-slate-400" />
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Live Preview
            </span>
          </div>

          <div className="bg-slate-100 rounded-2xl border border-slate-200 overflow-hidden shadow-inner flex flex-col h-[680px]">
            {/* Mock Mail Client Header */}
            <div className="bg-white border-b px-4 py-3 flex items-center gap-3">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
              </div>
              <div className="bg-slate-50 border rounded-lg text-xs text-slate-400 px-3 py-1 flex-1 text-center font-medium truncate max-w-xs mx-auto">
                Live Template Preview
              </div>
            </div>

            <div className="bg-slate-50 border-b px-6 py-3 space-y-1 text-xs">
              <div>
                <span className="text-slate-400 font-medium">To:</span> <span className="text-slate-700 font-semibold">employee@yourcompany.com</span>
              </div>
              <div>
                <span className="text-slate-400 font-medium">Subject:</span> <span className="text-slate-700 font-semibold">Action Required: Gap Assessment Review</span>
              </div>
            </div>

            {/* Email Canvas */}
            <div className="flex-1 bg-slate-100 p-4 overflow-y-auto">
              <iframe
                title="Email template live preview"
                srcDoc={getPreviewHtml()}
                className="w-full h-full border-0 bg-transparent rounded-lg"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
